import { invalidateOfflineCareForms } from "@/care-forms/hooks/useOfflineCareForms";
import { useSyncOfflineCareFormEdits } from "@/care-forms/hooks/useSyncCareFormEdits";
import {
	initializeCareFormDetailCaches,
	prefetchCareFormDetails,
	prefetchCareFormTypeOptions,
	prefetchCareFormTypePayerSourceOptions,
	prefetchCareFormTypePdfs,
} from "@/care-forms/utils/api";
import { clearOldCareFormDetails } from "@/care-forms/utils/storage";
import {
	usePrefetchCheckOutDetails,
	useSeedCaregiverAppShiftsCache,
	useSeedCheckOutDetailsCache,
	useSyncOfflineCheckInOuts,
} from "@/caregiver-app/components/hooks";
import { useClearOldCaregiverAppShiftsCache } from "@/caregiver-app/components/hooks";
import { useSyncOfflineOnboarding } from "@/caregiver-app/hooks/useSyncOfflineOnboarding";
import { prefetchClientOptions } from "@/clients/utils/api";
import { useIsOnline } from "@/common/hooks/use-is-online";
import { trpc } from "@/common/utils/trpc";
import { useLogger } from "@/logging/client/LoggerContext";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useGetCurrentUserQuery } from "./api/apiHooks";
import { useCacheStorage } from "./storage/CacheStorageContext";

const offlineQueriesEnabled =
	import.meta.env.VITE_CAPACITOR_BUILD === "true" ||
	import.meta.env.VITE_PWA_BUILD === "true";

export function OfflineDataSync() {
	const isOnline = useIsOnline();
	const syncPromise = useRef<Promise<void> | null>(null);
	const syncCounts = useRef(new Map<string, number>());
	const storage = useCacheStorage();
	const utils = trpc.useUtils();
	const currentUser = useGetCurrentUserQuery();
	const checkInOuts = useSyncOfflineCheckInOuts();
	const onboardingSync = useSyncOfflineOnboarding();
	const prefetchCheckOutDetails = usePrefetchCheckOutDetails();
	const clearOldShiftsCache = useClearOldCaregiverAppShiftsCache();
	const seedShiftsCache = useSeedCaregiverAppShiftsCache();
	const seedCheckOutDetailsCache = useSeedCheckOutDetailsCache();
	//const syncCareFormCreates = useSyncCareFormCreates();
	const syncCareFormEdits = useSyncOfflineCareFormEdits();
	const queryClient = useQueryClient();

	const isCaregiver = currentUser.data?.isCaregiver;
	const isOtherStaff =
		!currentUser.data?.isClient &&
		!currentUser.data?.isTrustedContact &&
		!currentUser.data?.isCaregiver;
	const logger = useLogger();

	useEffect(() => {
		async function sync() {
			if (isCaregiver) {
				// Seed the caches first so they're available immediately
				if (offlineQueriesEnabled && syncCounts.current.get("shifts") !== 1) {
					await seedShiftsCache
						.seedCache()
						.then(() => {
							syncCounts.current.set("shifts", 1);
						})
						.catch((error) => {
							logger.error(
								`Error seeding shifts cache. Error: ${error instanceof Error ? error.message : error}`,
							);
						});
				}
				if (
					offlineQueriesEnabled &&
					syncCounts.current.get("checkOutDetails") !== 1
				) {
					await seedCheckOutDetailsCache
						.seedCache()
						.then(() => {
							syncCounts.current.set("checkOutDetails", 1);
						})
						.catch((error) => {
							logger.error(
								`Error seeding check-out details cache. Error: ${error instanceof Error ? error.message : error}`,
							);
						});
				}

				await onboardingSync.sync();

				await checkInOuts.sync().catch((error) => {
					logger.error(
						`Error syncing check-in/out. Error: ${error instanceof Error ? error.message : error}`,
					);
				});

				if (offlineQueriesEnabled) {
					await clearOldShiftsCache.clearOldCache().catch((error) => {
						logger.error(
							`Error clearing old shifts cache. Error: ${error instanceof Error ? error.message : error}`,
						);
					});
					await prefetchCheckOutDetails.prefetch().catch((error) => {
						logger.error(
							`Error prefetching check-out details. Error: ${error instanceof Error ? error.message : error}`,
						);
					});
				}
			} else if (isOtherStaff) {
				// do this before syncing edits because edit sync could take a while
				if (
					offlineQueriesEnabled &&
					syncCounts.current.get("careFormDetails") !== 1
				) {
					await initializeCareFormDetailCaches(
						storage,
						utils,
						queryClient,
						logger,
					)
						.then(() => {
							syncCounts.current.set("careFormDetails", 1);
						})
						.catch((error) => {
							logger.error(
								`Error initializing care form detail caches. Error: ${error instanceof Error ? error.message : error}`,
							);
						});
				}
				// Sync care form creates before edits to ensure form exists for edits
				// removing offline create support for now as it's complicated and maybe not needed
				//await syncCareFormCreates();
				await syncCareFormEdits()
					.then(() => {
						invalidateOfflineCareForms(queryClient);
					})
					.catch((error) => {
						logger.error(
							`Error syncing care form edits. Error: ${error instanceof Error ? error.message : error}`,
						);
					});

				// only prefetch once per app session and if offline is supported
				if (
					offlineQueriesEnabled &&
					syncCounts.current.get("careFormDetails") !== 1
				) {
					await Promise.all([
						// removing offline create support for now as it's complicated and maybe not needed
						// prefetchClientOptions(storage, utils.client),
						// prefetchCareFormTypePayerSourceOptions(storage, utils),
						// prefetchCareFormTypeOptions(storage, utils),
						prefetchCareFormTypePdfs(storage, utils.client, logger),
						prefetchCareFormDetails(storage, utils, logger),
					])
						.then(() => {
							logger.info("Prefetched care form details");
						})
						.catch((error) => {
							logger.error(
								`Error prefetching care form details. Error: ${error instanceof Error ? error.message : error}`,
							);
						});
					invalidateOfflineCareForms(queryClient);
				}

				// browser version cache care form details too so this block is independent of offline queries
				if (syncCounts.current.get("careFormDetails") !== 1) {
					// cleanup old data last. prioritizing prefetching.
					await clearOldCareFormDetails(storage, 50, logger).catch((error) => {
						logger.error(
							`Error clearing old care form details. Error: ${error instanceof Error ? error.message : error}`,
						);
					});
				}
			}
		}

		let timeoutId: NodeJS.Timeout | null = null;
		function loop() {
			syncPromise.current = (syncPromise.current || Promise.resolve())
				.then(() => sync())
				.finally(() => {
					syncPromise.current = null;
					timeoutId = setTimeout(loop, 60000); // Run again in 1 minute
				});
		}
		loop();

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [isOnline, isCaregiver, isOtherStaff]);

	return null;
}
